"use client";

import { useMemo, useRef, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Notice,
  Panel,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, WIDGET_H2 } from "@/components/tool-kit/styles";
import { type Columns, zipColumns } from "@/components/tools/example-columns";
import {
  ContentionFields,
  RebuttalFields,
} from "@/components/tools/lit-essays/debate-fields";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { parseNumberOr } from "@/lib/parse-number";
import {
  blankContention,
  blankRebuttal,
  buildDebateCase,
  type Contention,
  caseWords,
  clock,
  DEBATE_FORMATS,
  DEBATE_SIDES,
  DEFAULT_MINUTES,
  DEFAULT_WPM,
  type DebateFormat,
  type DebateInput,
  type DebateSide,
  MAX_CONTENTIONS,
  MAX_REBUTTALS,
  MIN_CONTENTIONS,
  type RebuttalBlock,
  speechTiming,
} from "@/lib/tool-kit/lit-essays/debate-case";

/** English strings. A translated content file overrides these through `ui`. */
export const DEBATE_CASE_BUILDER_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  setupHeading: "The round",
  formatLabel: "Debate format",
  formats: {
    pf: "Public Forum",
    ld: "Lincoln–Douglas",
    policy: "Policy",
    classroom: "Classroom debate",
  },
  sideLabel: "Your side",
  sides: { aff: "Affirmative", neg: "Negative" },
  resolutionLabel: "Resolution",
  resolutionPlaceholder:
    "e.g. Resolved: Schools should start no earlier than 8:30 a.m.",
  definitionsLabel: "Definitions — one per line",
  definitionsPlaceholder: "e.g. Start — the first bell of the school day",
  definitionsHint:
    "Write “term — meaning”. Define only the words the round could turn on.",
  frameworkHeading: "Framework",
  frameworkLabels: {
    pf: "Framework — how the judge should weigh the round (optional)",
    ld: "Value — the ultimate good your side upholds",
    policy: "Plan text (affirmative) or your position (negative)",
    classroom: "Framework — how the round should be decided (optional)",
  },
  frameworkPlaceholders: {
    pf: "e.g. Weigh the round on which side better protects student well-being.",
    ld: "e.g. Justice",
    policy:
      "e.g. Public middle and high schools will set the first bell no earlier than 8:30 a.m.",
    classroom: "e.g. Decide on which side does more for student well-being.",
  },
  criterionLabel: "Value criterion — the standard that measures it",
  criterionPlaceholder: "e.g. Protecting individual rights",
  contentionsHeading: "Contentions",
  contention: {
    legend: "Contention {n}",
    taglineLabel: "Tagline — a short label",
    taglinePlaceholder: "e.g. Sleep health",
    claimLabel: "Claim — what you say is true",
    claimPlaceholder: "One sentence a judge could write on their flow.",
    warrantLabel: "Warrant — why the claim is true",
    warrantPlaceholder:
      "The reasoning, in a sentence or two, before you cite anyone.",
    evidenceLabel: "Evidence — one cited source per line",
    evidencePlaceholder:
      "e.g. American Academy of Pediatrics, 2014: recommends …",
    evidenceHint:
      "Name the author or organization and the year, then the fact, so the judge can ask for it.",
    impactLabel: "Impact — so what?",
    impactPlaceholder: "Who is affected, how badly, how likely, and how soon?",
    remove: "Remove",
    removeLabel: "Remove contention {n}",
  },
  addContention: "Add a contention",
  rebuttalsHeading: "Prepared rebuttal blocks",
  rebuttalsHint:
    "Answers to arguments you expect, written ahead of time. Add one for each strong objection.",
  rebuttal: {
    legend: "Rebuttal block {n}",
    theySayLabel: "They say …",
    theySayPlaceholder: "The opposing argument, fairly stated.",
    weRespondLabel: "We respond …",
    weRespondPlaceholder: "Your answer in one sentence.",
    becauseLabel: "Because …",
    becausePlaceholder: "The reason or evidence behind your answer.",
    impactLabel: "Therefore …",
    impactPlaceholder: "Why this matters to the vote.",
    remove: "Remove",
    removeLabel: "Remove rebuttal block {n}",
  },
  addRebuttal: "Add a rebuttal block",
  votersHeading: "Conclusion",
  votersLabel: "Voting issues — one per line",
  votersPlaceholder:
    "Why a judge should vote for your side, in a sentence each.",
  timingHeading: "Speech length",
  minutesLabel: "Speech length in minutes",
  minutesHint:
    "Typical first speeches: Public Forum 4, Lincoln–Douglas affirmative 6, Policy 8. Check your league’s rules.",
  wpmLabel: "Speaking pace in words per minute",
  wpmHint: "A clear delivery is about 150. Competitive speakers go faster.",
  wordsLabel: "Words in your case",
  estimateLabel: "Estimated delivery time",
  allowedLabel: "Time allowed",
  capacityLabel: "Words that fit",
  timeStates: {
    empty: "Write your case to see how long it takes to deliver.",
    under: "Under time. There is room for more evidence or weighing.",
    ok: "Within time at this pace.",
    over: "Over time at this pace. Cut the weakest evidence before you speed up.",
  },
  fileName: "debate-case",
  document: {
    heading: "Your debate case",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part of the case has your content.",
  },
  docTitle: {
    aff: "Affirmative Constructive",
    neg: "Negative Constructive",
  },
  subtitle: "{format} · case outline",
  headings: {
    resolution: "Resolution",
    introduction: "Introduction and framing",
    definitions: "Definitions",
    contention: "Contention {n}: {tagline}",
    contentionBlank: "Contention {n}",
    rebuttals: "Prepared rebuttal blocks",
    block: "Block {n}",
    conclusion: "Conclusion: voting issues",
  },
  items: {
    roadmap: "Roadmap",
    claim: "Claim",
    warrant: "Warrant",
    evidence: "Evidence",
    impact: "Impact",
    theySay: "They say",
    weRespond: "We respond",
    because: "Because",
    therefore: "Therefore",
    voters: "Voting issues",
    value: "Value",
    criterion: "Value criterion",
  },
  framework: {
    pf: {
      heading: "Framework",
      prompt:
        "Framework (optional) — tell the judge how to weigh the round, or skip it if your impacts speak for themselves.",
    },
    ld: {
      heading: "Value",
      prompt:
        "Value — name the ultimate good your side upholds, such as justice or the common good.",
    },
    policy: {
      heading: "Plan",
      prompt:
        "Plan text — state the specific action the affirmative proposes (negative: state your position or counterplan).",
    },
    classroom: {
      heading: "Framework",
      prompt: "Framework (optional) — say how the round should be decided.",
    },
  },
  prompts: {
    resolution: "Paste the resolution exactly as it is worded.",
    definitions:
      "Define the words in the resolution that the round could turn on.",
    roadmap:
      "Roadmap — name your contentions in the order you will present them.",
    claim: "Claim — one sentence that says what is true.",
    warrant: "Warrant — the reason the claim is true.",
    evidence: "Evidence — add at least one cited source, one per line.",
    impact:
      "Impact — what follows, how large, how likely, and how soon; and why it outweighs the other side.",
    rebuttal: "Fill in this part of the block.",
    voters:
      "Voting issues — one sentence each on why the judge should vote for you.",
    criterion:
      "Value criterion — the standard that tells the judge whether the value is upheld.",
  },
  guidance: {
    contention:
      "Each contention runs claim, warrant, evidence, impact. Read the claim as a tagline, give the reason, cite the evidence, then say why it matters.",
    rebuttals:
      "Signpost every answer — “They say …, we respond …” — so the judge can follow it on the flow.",
    definitions:
      "Cite where each definition comes from if the round could turn on it.",
  },
  roadmapLine: "my case has these contentions: {list}.",
  contentionStand: "Contention {n}",
  and: "and",
  example: {
    format: "pf",
    side: "aff",
    resolution:
      "Resolved: Public schools should start no earlier than 8:30 a.m.",
    definitions:
      "Start — the first bell of the school day\nPublic schools — publicly funded middle and high schools",
    frameworkMain:
      "Weigh the round on which side better protects student health and learning.",
    frameworkCriterion: "",
    voters:
      "Student health and learning outweigh the logistical cost of changing bell times.\nThe policy is already working in practice.",
  },
  exampleContentions: {
    tagline: ["Sleep health", "It is workable"],
    claim: [
      "Later start times improve teenagers’ sleep.",
      "Later start times can be put into practice.",
    ],
    warrant: [
      "Adolescents’ body clocks shift later at puberty, so an early bell forces many teens to wake before their bodies are ready.",
      "Policies like this one already exist, so districts have a model to follow.",
    ],
    evidence: [
      "American Academy of Pediatrics, 2014 policy statement “School Start Times for Adolescents”: recommends that middle and high schools start no earlier than 8:30 a.m.",
      "California’s SB 328 (2019) requires most public high schools to start no earlier than 8:30 a.m. and middle schools no earlier than 8:00 a.m.",
    ],
    impact: [
      "Better-rested students are healthier and more alert, so a later bell improves health and learning for the students it covers.",
      "This answers the main practical objection: districts can adapt their schedules.",
    ],
  } as Columns<"tagline" | "claim" | "warrant" | "evidence" | "impact">,
  exampleRebuttals: {
    theySay: ["Later starts hurt after-school sports and jobs."],
    weRespond: [
      "Schedules can adjust: practices, games, and work shifts can move with the bell.",
    ],
    because: [
      "Districts that changed their start times had to rework those schedules, and the policy still stands.",
    ],
    impact: [
      "The cost is logistics, while the benefit is student health, so the benefit outweighs the cost.",
    ],
  } as Columns<"theySay" | "weRespond" | "because" | "impact">,
} as const;

type Strings = typeof DEBATE_CASE_BUILDER_STRINGS;

/** The example's contentions, text only; English fills any short translated column. */
export function debateExampleContentions(
  strings: Strings
): Omit<Contention, "id">[] {
  return zipColumns(
    DEBATE_CASE_BUILDER_STRINGS.exampleContentions,
    strings.exampleContentions
  );
}

/** The example's rebuttal blocks, text only; English fills any short column. */
export function debateExampleRebuttals(
  strings: Strings
): Omit<RebuttalBlock, "id">[] {
  return zipColumns(
    DEBATE_CASE_BUILDER_STRINGS.exampleRebuttals,
    strings.exampleRebuttals
  );
}
type TextKey =
  | "resolution"
  | "definitions"
  | "frameworkMain"
  | "frameworkCriterion"
  | "voters";

const BLANK: Pick<DebateInput, TextKey> = {
  resolution: "",
  definitions: "",
  frameworkMain: "",
  frameworkCriterion: "",
  voters: "",
};

function TimingPanel({
  words,
  minutesText,
  wpmText,
  strings,
}: {
  words: number;
  minutesText: string;
  wpmText: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const minutes = parseNumberOr(minutesText, 0);
  const wpm = parseNumberOr(wpmText, DEFAULT_WPM);
  const timing = speechTiming(words, minutes, wpm);
  return (
    <div aria-live="polite" className="space-y-3">
      <StatGrid>
        <StatCard label={strings.wordsLabel} value={fmt(timing.words)} />
        <StatCard
          emphasis
          label={strings.estimateLabel}
          value={clock(timing.seconds)}
        />
        <StatCard
          label={strings.allowedLabel}
          value={clock(timing.budgetSeconds)}
        />
        <StatCard label={strings.capacityLabel} value={fmt(timing.capacity)} />
      </StatGrid>
      <Notice>{strings.timeStates[timing.state]}</Notice>
    </div>
  );
}

export default function DebateCaseBuilderWidget() {
  const strings = useToolUi(DEBATE_CASE_BUILDER_STRINGS);
  const fmt = useToolNumber();
  const nextContention = useRef(MIN_CONTENTIONS + 1);
  const nextRebuttal = useRef(1);
  const [format, setFormat] = useState<DebateFormat>("pf");
  const [side, setSide] = useState<DebateSide>("aff");
  const [fields, setFields] = useState(BLANK);
  const [contentions, setContentions] = useState<Contention[]>([
    blankContention(1),
    blankContention(2),
  ]);
  const [rebuttals, setRebuttals] = useState<RebuttalBlock[]>([
    blankRebuttal(1),
  ]);
  const [minutesText, setMinutesText] = useState(String(DEFAULT_MINUTES.pf));
  const [wpmText, setWpmText] = useState(String(DEFAULT_WPM));
  const input: DebateInput = useMemo(
    () => ({ ...fields, format, side, contentions, rebuttals }),
    [fields, format, side, contentions, rebuttals]
  );
  const doc = useMemo(() => buildDebateCase(input, strings), [input, strings]);
  const update = (key: TextKey) => (value: string) =>
    setFields((previous) => ({ ...previous, [key]: value }));
  const area = (
    key: TextKey,
    label: string,
    placeholder: string,
    hint?: string,
    rows = 2
  ) => (
    <TextAreaField
      hint={hint}
      id={`dc-${key}`}
      label={label}
      onChange={update(key)}
      placeholder={placeholder}
      prose
      rows={rows}
      value={fields[key]}
    />
  );
  const freshContention = () => {
    nextContention.current += 1;
    return blankContention(nextContention.current);
  };
  const freshRebuttal = () => {
    nextRebuttal.current += 1;
    return blankRebuttal(nextRebuttal.current);
  };

  function chooseFormat(value: DebateFormat) {
    setFormat(value);
    setMinutesText(String(DEFAULT_MINUTES[value]));
  }

  function loadExample() {
    const { example } = strings;
    const exampleContentions = debateExampleContentions(strings);
    const exampleRebuttals = debateExampleRebuttals(strings);
    const { format: exampleFormat, side: exampleSide, ...rest } = example;
    chooseFormat(exampleFormat);
    setSide(exampleSide);
    setFields({ ...BLANK, ...rest });
    setContentions(
      exampleContentions.map((entry) => ({ ...freshContention(), ...entry }))
    );
    setRebuttals(
      exampleRebuttals.map((entry) => ({ ...freshRebuttal(), ...entry }))
    );
  }

  function clear() {
    setFields(BLANK);
    setContentions([freshContention(), freshContention()]);
    setRebuttals([freshRebuttal()]);
  }

  return (
    <div className="space-y-8">
      <div className="space-y-6 print:hidden">
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={loadExample}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button className={BUTTON_SECONDARY} onClick={clear} type="button">
            {strings.clear}
          </button>
        </div>
        <Panel title={strings.setupHeading}>
          <div className="grid gap-5 sm:grid-cols-2">
            <PillGroup
              label={strings.formatLabel}
              name="dc-format"
              onChange={chooseFormat}
              options={DEBATE_FORMATS.map((value) => ({
                value,
                label: strings.formats[value],
              }))}
              value={format}
            />
            <PillGroup
              label={strings.sideLabel}
              name="dc-side"
              onChange={setSide}
              options={DEBATE_SIDES.map((value) => ({
                value,
                label: strings.sides[value],
              }))}
              value={side}
            />
          </div>
          {area(
            "resolution",
            strings.resolutionLabel,
            strings.resolutionPlaceholder
          )}
          {area(
            "definitions",
            strings.definitionsLabel,
            strings.definitionsPlaceholder,
            strings.definitionsHint
          )}
        </Panel>
        <Panel title={strings.frameworkHeading}>
          {area(
            "frameworkMain",
            strings.frameworkLabels[format],
            strings.frameworkPlaceholders[format]
          )}
          {format === "ld"
            ? area(
                "frameworkCriterion",
                strings.criterionLabel,
                strings.criterionPlaceholder
              )
            : null}
        </Panel>
        <section className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.contentionsHeading}</h2>
          {contentions.map((contention, index) => (
            <ContentionFields
              contention={contention}
              key={contention.id}
              number={fmt(index + 1)}
              onChange={(change) =>
                setContentions((previous) =>
                  previous.map((entry) =>
                    entry.id === contention.id ? { ...entry, ...change } : entry
                  )
                )
              }
              onRemove={() =>
                setContentions((previous) =>
                  previous.filter((entry) => entry.id !== contention.id)
                )
              }
              removable={contentions.length > MIN_CONTENTIONS}
              strings={strings.contention}
            />
          ))}
          {contentions.length < MAX_CONTENTIONS ? (
            <button
              className={BUTTON_SECONDARY}
              onClick={() =>
                setContentions((previous) => [...previous, freshContention()])
              }
              type="button"
            >
              {strings.addContention}
            </button>
          ) : null}
        </section>
        <section className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.rebuttalsHeading}</h2>
          <p className="text-muted-foreground text-sm">
            {strings.rebuttalsHint}
          </p>
          {rebuttals.map((block, index) => (
            <RebuttalFields
              block={block}
              key={block.id}
              number={fmt(index + 1)}
              onChange={(change) =>
                setRebuttals((previous) =>
                  previous.map((entry) =>
                    entry.id === block.id ? { ...entry, ...change } : entry
                  )
                )
              }
              onRemove={() =>
                setRebuttals((previous) =>
                  previous.filter((entry) => entry.id !== block.id)
                )
              }
              strings={strings.rebuttal}
            />
          ))}
          {rebuttals.length < MAX_REBUTTALS ? (
            <button
              className={BUTTON_SECONDARY}
              onClick={() =>
                setRebuttals((previous) => [...previous, freshRebuttal()])
              }
              type="button"
            >
              {strings.addRebuttal}
            </button>
          ) : null}
        </section>
        <Panel title={strings.votersHeading}>
          {area("voters", strings.votersLabel, strings.votersPlaceholder)}
        </Panel>
        <Panel title={strings.timingHeading}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              hint={strings.minutesHint}
              id="dc-minutes"
              label={strings.minutesLabel}
              numeric
              onChange={setMinutesText}
              value={minutesText}
            />
            <TextField
              hint={strings.wpmHint}
              id="dc-wpm"
              label={strings.wpmLabel}
              numeric
              onChange={setWpmText}
              value={wpmText}
            />
          </div>
          <TimingPanel
            minutesText={minutesText}
            strings={strings}
            words={caseWords(input)}
            wpmText={wpmText}
          />
        </Panel>
      </div>
      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="dc"
        labels={strings.document}
      />
    </div>
  );
}
